//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  itemWidth: number | string;
}

/**
 * The ContentCardGrid organism displays its children prop
 * in a grid with standard spacing between each child node. The
 * grid is created by positioning as many child items as possible
 * in a single row, then breaking onto new rows when there is not
 * enough additional space left.
 *
 * Note: When dealing with large sets of infinite scroll data,
 * consider using the VirtualizedContentCardGrid component
 * instead.
 *
 * Note: The itemWidth prop must be provided as we use it to size
 * each column. In most circumstances, this value should match the
 * static width of each child in the children prop.
 *
 * @example
 *   const someWidth = 300;
 *
 *   <ContentCardGrid itemWidth={ someWidth }>
 *     <SomeMediaCard />
 *     <SomeMediaCard />
 *     ...
 *   </ContentCardGrid>
 *
 *   const SomeMediaCard = styled(MediaCard)`
 *     width: ${someWidth}px;
 *   `;
 */
export const ContentCardGrid = ({ children, className, itemWidth }: Props) => {
  const itemWidthStr =
    typeof itemWidth === 'number' ? `${itemWidth}px` : itemWidth;
  return (
    <Grid className={className} itemWidth={itemWidthStr}>
      {children}
    </Grid>
  );
};

export const Grid = styled.div<{ itemWidth: string }>(
  ({ itemWidth, theme }) => `
  /*
   * Our use-case does not fall squarely into the grid or flexbox
   * display types. Using grid here means we must supply the column
   * width via a prop, but allows us to uniformly space cards without
   * relying on margin/size/positioning hacks.
   */
  display: grid;
  grid-gap: ${theme.spacings[3]};

  /*
   * Automatically create as many columns as will fit in the available
   * horizontal space. We use auto-fit here so that additional empty
   * columns are created when there is enough space to fit an additional
   * column but there is no more content toplace in it.
   */
  grid-template-columns: repeat(auto-fit, ${itemWidth});
  width: 100%;
  /**
   * Wrap in a small amount space to account for box shadows
   * on nested elements. Note this spacing will be accounted
   * for in designs that include this component.
   */
  padding: ${theme.spacings[2]} 0;
`,
);

export default ContentCardGrid;
